import _ from 'lodash';

import { type FilterConfig } from './condition-summary';

/**
 * A stored column name can be a comma list, and the comma is part of the name.
 *
 * <p>A real chip on the quotes listing reads `filter[isRenewal,isMigration][bo][0]=1`: one column
 * whose name is two columns, meaning "either of these". The service stores it, the listing filters
 * on it, and `chip-payload` deliberately leaves it alone when rewriting a saved query.
 *
 * <p>Nothing else knows about it. `FilterModal` builds its column picker from the keys of the
 * table's config, where no such key exists, so the row rendered a blank name, a disabled operator
 * and a bare `1` -- and there was no way to repair it, because the column could not be picked.
 */
export const COMBINED_COLUMN_SEPARATOR = ',';

/** The real columns a stored column name refers to. One for an ordinary column. */
export const partsOfColumn = (column: string): readonly string[] =>
  (column ?? '').split(COMBINED_COLUMN_SEPARATOR).filter(Boolean);

export const isCombinedColumn = (column: string): boolean =>
  partsOfColumn(column).length > 1;

/**
 * One column's label, however many real columns it names.
 *
 * <p>Each part is labelled on its own, because neither the config nor `startCase` has an entry for
 * the pair -- "Renewal / Migration" rather than "Is Renewal,is Migration".
 */
export const columnLabelOf = (
  column: string,
  config: FilterConfig = {}
): string =>
  partsOfColumn(column)
    .map((part) => config?.[part]?.text ?? _.startCase(part))
    .join(' / ');

/**
 * The table's config, plus an entry for every combined column the query actually uses.
 *
 * <p>Derived from the query rather than declared, because which pairs exist is decided by whoever
 * saved the chip, not by the listing. Only the pairs in play are added, so the column picker does
 * not grow a combination nobody uses.
 *
 * <p>The first part supplies everything but the label: not only the type, which decides the
 * operator wording and which value widget renders, but the widget's own options -- without them
 * the value select has nothing to match the stored value against and reads "Select" on a filter
 * that is in fact applied. A pair of columns of different types would be a query the modal could
 * not have built, and the first part is still a better answer than none.
 */
export const withCombinedColumns = (
  config: FilterConfig = {},
  columns: readonly string[] = []
): FilterConfig => {
  const combined = _.uniq(
    (columns ?? []).filter(
      (column) => column && isCombinedColumn(column) && !config?.[column]
    )
  );

  if (combined.length === 0) {
    return config ?? {};
  }

  const extras: Record<string, Record<string, unknown>> = {};
  combined.forEach((column) => {
    extras[column] = {
      ...(config?.[partsOfColumn(column)[0]] ?? {}),
      text: columnLabelOf(column, config),
    };
  });

  return { ...config, ...extras };
};
